import Service from '@ember/service';
import config from 'embroil/config/environment';

export default class TwitchEmbedService extends Service {
  /**
   * Gets the appropriate parent domain for Twitch embeds based on environment
   */
  get parentDomain(): string {
    return config.twitchParentUrl || 'localhost';
  }

  /**
   * Generates a Twitch channel embed URL
   * @param channel - The Twitch channel name
   * @param options - Additional options for the embed
   */
  getChannelEmbedUrl(
    channel: string,
    options: {
      autoplay?: boolean;
      muted?: boolean;
      width?: string | number;
      height?: string | number;
    } = {}
  ): string {
    const { autoplay = false, muted = true } = options;
    const params = new URLSearchParams({
      channel,
      parent: this.parentDomain,
      autoplay: String(autoplay),
      muted: String(muted),
      allowfullscreen: 'true',
      theme: 'dark'
    });

    return `https://player.twitch.tv/?${params.toString()}`;
  }

  /**
   * Generates a Twitch video embed URL
   * @param videoId - The Twitch video ID (e.g., 'v417077870')
   * @param options - Additional options for the embed
   */
  getVideoEmbedUrl(
    videoId: string,
    options: {
      autoplay?: boolean;
      muted?: boolean;
      time?: string;
      width?: string | number;
      height?: string | number;
    } = {}
  ): string {
    const { autoplay = false, muted = true, time } = options;
    const params = new URLSearchParams({
      video: videoId,
      parent: this.parentDomain,
      autoplay: String(autoplay),
      muted: String(muted),
    });

    if (time) {
      params.set('t', time);
    }

    return `https://player.twitch.tv/?${params.toString()}`;
  }

  /**
   * Generates a complete iframe HTML string for Twitch embeds
   * @param url - The Twitch embed URL
   * @param options - HTML attributes for the iframe
   */
  generateIframeHtml(
    url: string,
    options: {
      width?: string | number;
      height?: string | number;
      allowfullscreen?: boolean;
      frameborder?: string | number;
      scrolling?: string;
    } = {}
  ): string {
    const {
      width = '100%',
      height = '158',
      allowfullscreen = true,
      frameborder = '0',
      scrolling = 'no',
    } = options;

    const attributes = [
      `width="${width}"`,
      `height="${height}"`,
      `src="${url}"`,
      `frameborder="${frameborder}"`,
      `scrolling="${scrolling}"`,
      allowfullscreen ? 'allowfullscreen' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return `<iframe ${attributes}></iframe>`;
  }

  /**
   * Convenience method to generate a channel iframe HTML
   */
  getChannelIframeHtml(
    channel: string,
    options: {
      width?: string | number;
      height?: string | number;
      autoplay?: boolean;
      muted?: boolean;
      allowfullscreen?: boolean;
    } = {}
  ): string {
    const url = this.getChannelEmbedUrl(channel, options);
    return this.generateIframeHtml(url, options);
  }

  /**
   * Convenience method to generate a video iframe HTML
   */
  getVideoIframeHtml(
    videoId: string,
    options: {
      width?: string | number;
      height?: string | number;
      time?: string;
      autoplay?: boolean;
      muted?: boolean;
      allowfullscreen?: boolean;
    } = {}
  ): string {
    const url = this.getVideoEmbedUrl(videoId, options);
    return this.generateIframeHtml(url, options);
  }
}

// Extend the service registry to include type information
declare module '@ember/service' {
  interface Registry {
    'twitch-embed': TwitchEmbedService;
  }
}